import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const notificationsTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isWS =
    stack.realtime === 'websockets' || stack.realtime === 'supabase-realtime';
  const isSSE = stack.realtime === 'sse';
  const isPusher = stack.realtime === 'pusher-ably';
  const isMongo = stack.database === 'mongodb';

  const channels = (featureConfig?.options['delivery-channels'] as
    | string[]
    | undefined) ?? ['in-app', 'email'];
  const hasEmail = channels.includes('email');
  const hasPush = channels.includes('push');

  return {
    pages: [
      {
        id: 'notifications',
        path: '/notifications',
        label: 'Notifications',
        description: 'Full notification history and mark-as-read',
        authRequired: true,
      },
      {
        id: 'notification-settings',
        path: '/settings/notifications',
        label: 'Notification preferences',
        description: 'Toggle per-type and per-channel notification settings',
        authRequired: true,
      },
    ],
    uxFlows: [
      {
        id: 'receive-notification',
        title: 'User receives a notification',
        steps: isWS
          ? [
              'Server event triggers notification creation in DB',
              'POST /api/notifications inserts record',
              'Server emits notification event over WebSocket to user room',
              'Client increments unread badge and appends to notification list',
            ]
          : isSSE
            ? [
                'Server event triggers notification creation in DB',
                'POST /api/notifications inserts record',
                'Server sends event on user SSE stream',
                'Client updates unread count and shows notification',
              ]
            : isPusher
              ? [
                  'Server event triggers notification creation in DB',
                  'Server triggers Pusher event on user-{id} channel',
                  'Client receives push and updates notification badge',
                ]
              : [
                  'Server event triggers notification creation in DB',
                  'Client polls GET /api/notifications/unread-count every 30s',
                  'Badge updates on next poll; user opens /notifications to view',
                ],
      },
      {
        id: 'mark-read',
        title: 'User marks notifications as read',
        steps: [
          'User opens notification tray or /notifications page',
          'PATCH /api/notifications/read-all marks all as read',
          'Unread count resets to zero in UI',
        ],
      },
    ],
    endpoints: [
      {
        id: 'get-notifications',
        method: 'GET',
        path: '/api/notifications',
        description: 'Paginated notification history for current user',
        response: '{ notifications: Notification[], unreadCount: number }',
      },
      {
        id: 'unread-count',
        method: 'GET',
        path: '/api/notifications/unread-count',
        description: 'Fast unread badge count (used by polling)',
        response: '{ count: number }',
      },
      {
        id: 'mark-read',
        method: 'PATCH',
        path: '/api/notifications/read-all',
        description: 'Mark all notifications as read',
      },
      {
        id: 'mark-one-read',
        method: 'PATCH',
        path: '/api/notifications/:id/read',
        description: 'Mark a single notification as read',
      },
      ...(isWS
        ? [
            {
              id: 'ws',
              method: 'GET' as const,
              path: '/ws',
              description:
                'WebSocket — server pushes notification events to connected user',
            },
          ]
        : []),
      ...(isSSE
        ? [
            {
              id: 'sse',
              method: 'GET' as const,
              path: '/api/notifications/stream',
              description: 'SSE stream — server pushes notification events',
            },
          ]
        : []),
    ],
    dbTables: isMongo
      ? [
          {
            id: 'notifications',
            name: 'notifications',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'userId', type: 'ObjectId ref users', nullable: false },
              {
                name: 'type',
                type: 'string',
                nullable: false,
                notes: 'e.g. message, mention, follow',
              },
              {
                name: 'data',
                type: 'object',
                nullable: false,
                notes: 'Arbitrary payload per type',
              },
              { name: 'read', type: 'boolean', nullable: false },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
          },
          ...(hasEmail || hasPush
            ? [
                {
                  id: 'notification_prefs',
                  name: 'notification_prefs',
                  columns: [
                    { name: '_id', type: 'ObjectId', nullable: false },
                    {
                      name: 'userId',
                      type: 'ObjectId ref users',
                      nullable: false,
                    },
                    { name: 'type', type: 'string', nullable: false },
                    { name: 'email', type: 'boolean', nullable: false },
                    { name: 'push', type: 'boolean', nullable: false },
                    { name: 'inApp', type: 'boolean', nullable: false },
                  ],
                },
              ]
            : []),
        ]
      : [
          {
            id: 'notifications',
            name: 'notifications',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              { name: 'user_id', type: 'uuid FK users', nullable: false },
              {
                name: 'type',
                type: 'text',
                nullable: false,
                notes: 'e.g. message, mention, follow',
              },
              {
                name: 'data',
                type: 'jsonb',
                nullable: false,
                notes: 'Arbitrary payload per type',
              },
              { name: 'read', type: 'boolean', nullable: false },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes: 'Index on (user_id, read) for badge count query',
          },
          ...(hasEmail || hasPush
            ? [
                {
                  id: 'notification_prefs',
                  name: 'notification_prefs',
                  columns: [
                    { name: 'user_id', type: 'uuid FK users', nullable: false },
                    { name: 'type', type: 'text', nullable: false },
                    { name: 'email', type: 'boolean', nullable: false },
                    { name: 'push', type: 'boolean', nullable: false },
                    { name: 'in_app', type: 'boolean', nullable: false },
                  ],
                  notes: 'Composite PK (user_id, type)',
                },
              ]
            : []),
        ],
  };
};
